import type { ErrorHandler} from '@angular/core';
import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { AuthStore } from '../auth/auth.store';
import { TenantContextService } from '../tenant/tenant-context.service';
import {
  ErrorReporter,
  type ErrorContext,
  SloBeaconService,
} from '../observability/observability.public-api';

import { isChunkLoadError } from './chunk-load-error';
import { ToastBusService } from './toast-bus.service';

/**
 * ErrorHandler global. Última red de contención de errores no manejados.
 *
 * - Los HttpErrorResponse ya los toastea el httpErrorInterceptor → acá se ignoran
 *   para no duplicar el aviso al operador (ni reportar 4xx esperados como si fueran
 *   crashes).
 * - Todo lo demás (excepciones de runtime, chunk-load fallido, etc.) se loguea CON
 *   CONTEXTO (ruta, usuario, tenant), se muestra un toast y se hace fan-out a los
 *   sinks de observabilidad:
 *     · el RUM/SloBeacon (señal de degradación, gateado por sampling), y
 *     · el ErrorReporter externo (Sentry), que sólo reporta si hay DSN configurado.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly toasts = inject(ToastBusService);
  private readonly reporter = inject(ErrorReporter);
  private readonly beacon = inject(SloBeaconService);
  private readonly auth = inject(AuthStore);
  private readonly tenant = inject(TenantContextService);

  handleError(error: unknown): void {
    // Los errores HTTP ya se notificaron en el interceptor de error.
    if (error instanceof HttpErrorResponse) {
      return;
    }

    const unwrapped = this.unwrap(error);
    const context = this.context();

    // Chunk load falla en despliegues nuevos → sugerir recargar (PWA/lazy).
    if (isChunkLoadError(unwrapped)) {
      this.toasts.warning(
        'Nueva versión disponible',
        'Recargá la página para actualizar la aplicación.',
      );
      // eslint-disable-next-line no-console
      console.error('[GlobalErrorHandler] chunk load error', unwrapped, context);
      this.dispatch('chunk-load', unwrapped, context);
      return;
    }

    // eslint-disable-next-line no-console
    console.error('[GlobalErrorHandler]', unwrapped, context);
    this.toasts.error(
      'Ocurrió un error inesperado',
      unwrapped instanceof Error ? unwrapped.message : String(unwrapped),
    );
    this.dispatch('unhandled', unwrapped, context);
  }

  /**
   * Fan-out a los sinks de observabilidad:
   *  - RUM/SloBeacon: cuenta el error como degradación (el propio beacon respeta
   *    el sampling, así que llamar siempre es seguro).
   *  - ErrorReporter: reporta al colector externo SÓLO si hay DSN (gating interno).
   */
  private dispatch(name: string, error: unknown, context: ErrorContext): void {
    this.beacon.reportError(name);
    this.reporter.report(error, context);
  }

  /** Contexto de correlación con el backend. `pathname` (sin query) evita filtrar PII. */
  private context(): ErrorContext {
    return {
      route: typeof location !== 'undefined' ? location.pathname : '',
      userId: this.auth.user()?.id ?? null,
      tenantId: this.tenant.tenantId(),
    };
  }

  private unwrap(error: unknown): unknown {
    // Angular envuelve errores de zona/promesa en `.rejection`.
    if (error && typeof error === 'object' && 'rejection' in error) {
      return (error as { rejection: unknown }).rejection;
    }
    return error;
  }
}
